import jsPDF from "jspdf"
import "jspdf-autotable"
import { deliverPdf } from "./helpers/pdfDelivery.js"
import { drawPdfLogo } from "./helpers/pdfLogo.js"

const formatCurrency = (value) => {
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    maximumFractionDigits: 0,
  }).format(Number(value) || 0)
}

const formatDate = (dateString) => {
  if (!dateString) return "N/A"
  const dateObj = new Date(dateString)
  if (isNaN(dateObj.getTime())) return String(dateString)
  return new Intl.DateTimeFormat("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(dateObj)
}


const getVendedoresTexto = (row) => {
  const cantidad = Number(row?.cantidadVendedoresBom ?? 0)
  if (cantidad <= 0) return "-"
  if (cantidad === 1) return row?.primerVendedorBom || "-"
  const lista = row?.vendedoresBomNombres ?? []
  return lista.length ? lista.join("\n") : "-"
}

export const generarPdfTablaReceta = ({
  recetas = [],
  filtros = "",
  logoBase64 = null,
  usuario = "Usuario",
  deliveryContext = null,
}) => {
  if (!recetas.length) return false

  const doc = new jsPDF({ unit: "pt", format: "a4", orientation: "landscape" })
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const margin = 20

  const PAL = {
    primary: [25, 118, 210],
    ink: [33, 33, 33],
    muted: [117, 117, 117],
    inkLight: [158, 158, 158],
    bgSoft: [245, 247, 250],
  }

  drawPdfLogo({ doc, logoBase64, x: margin, y: margin, maxW: 80, maxH: 36 })

  let yPosition = 36

  doc.setFont("helvetica", "bold")
  doc.setFontSize(16)
  doc.setTextColor(...PAL.primary)
  doc.text("Reporte de grupo / viaje", pageWidth / 2, yPosition, { align: "center" })
  yPosition += 20

  doc.setFont("helvetica", "normal")
  doc.setFontSize(9)
  doc.setTextColor(...PAL.inkLight)
  doc.text(
    `Fecha de generación: ${formatDate(new Date())} | Usuario: ${usuario || "Usuario"}`,
    pageWidth - margin,
    yPosition,
    { align: "right" }
  )
  yPosition += 24

  doc.setFont("helvetica", "bold")
  doc.setFontSize(11)
  doc.setTextColor(...PAL.ink)
  doc.text("Filtros aplicados:", margin, yPosition)
  yPosition += 14

  doc.setDrawColor(...PAL.primary)
  doc.setLineWidth(1)
  doc.line(margin, yPosition, pageWidth - margin, yPosition)
  yPosition += 12

  doc.setFont("helvetica", "normal")
  doc.setFontSize(9)
  doc.setTextColor(...PAL.muted)
  doc.text(String(filtros || "Sin filtros aplicados"), margin, yPosition, {
    maxWidth: pageWidth - margin * 2,
  })
  yPosition += 24

  doc.setFillColor(...PAL.bgSoft)
  doc.roundedRect(margin, yPosition - 13, 240, 20, 3, 3, "F")
  doc.setFont("helvetica", "bold")
  doc.setFontSize(10)
  doc.setTextColor(...PAL.ink)
  doc.text(`TOTAL DE REGISTROS: ${recetas.length}`, margin + 8, yPosition)
  yPosition += 24

  const head = [
    "Fecha Viaje",
    "Grupo",
    "Destino",
    "Precio",
    "Servicios",
    "Butacas",
    "Pasajeros",
    "Vendedor",
    "Observaciones",
  ]

  const body = recetas.map((row) => {
    const fechaViaje = row.fechaInicio ?? row.fechaCreacion ?? "N/A"
    const grupo = row.codigoProductoTerminado ?? "N/A"
    const destino = row.destino ?? "N/A"
    const monto = row.monto ?? row.precioPaquete ?? 0
    const servicios = Number(row.totalIngredientes ?? 0)
    const butacas = Number(row.butacas ?? 0)
    const pasajeros = Number(row.pasajeros ?? 0)
    const vendedorTexto = getVendedoresTexto(row)
    const observaciones = row.observaciones ?? ""
    return [
      fechaViaje,
      grupo,
      destino,
      formatCurrency(monto),
      String(servicios),
      String(butacas),
      String(pasajeros),
      vendedorTexto,
      observaciones,
    ]
  })

  doc.autoTable({
    startY: yPosition,
    head: [head],
    body,
    theme: "grid",
    headStyles: {
      fillColor: PAL.primary,
      fontSize: 9,
      fontStyle: "bold",
      halign: "center",
      textColor: [255, 255, 255],
    },
    bodyStyles: {
      fontSize: 8,
      cellPadding: 4,
      textColor: PAL.ink,
      valign: "top",
    },
    alternateRowStyles: {
      fillColor: [250, 250, 250],
    },
    columnStyles: {
      0: { cellWidth: 70, halign: "center" },
      1: { cellWidth: 90, overflow: "linebreak" },
      2: { cellWidth: 90, overflow: "linebreak" },
      3: { cellWidth: 70, halign: "right" },
      4: { cellWidth: 50, halign: "center" },
      5: { cellWidth: 50, halign: "center" },
      6: { cellWidth: 50, halign: "center" },
      7: { cellWidth: 90, overflow: "linebreak", minCellHeight: 20 },
      8: { cellWidth: 110, overflow: "linebreak" },
    },
    margin: { left: margin, right: margin },
    didParseCell: (data) => {
      if (data.column.index === 7 && data.section === "body") {
        const texto = String(data.cell.raw ?? "")
        const lineCount = (texto.match(/\n/g) || []).length + 1
        if (lineCount > 1) {
          data.cell.minCellHeight = 8 + lineCount * 6
        }
      }
    },
    didDrawPage: (data) => {
      doc.setFont("helvetica", "normal")
      doc.setFontSize(8)
      doc.setTextColor(...PAL.inkLight)
      const pageText = `Página ${data.pageNumber} de ${doc.internal.pages.length - 1}`
      doc.text(pageText, pageWidth / 2, pageHeight - 20, { align: "center" })
    },
  })

  const fileName = `grupos_${new Date().toISOString().slice(0, 10)}.pdf`
  doc.setProperties({ title: fileName, subject: "Reporte de Grupos / Viaje", creator: "Sistema" })
  deliverPdf({ doc, fileName, targetWindow: deliveryContext?.targetWindow || null })
  return true
}
